import { QuestionType } from '@features/event-registration/types';

import { enrichMemberFormFields } from '../components/wizard-steps/fields-step/helpers/memberFieldPicker';
import { FormFieldCategory, FormFieldData } from '../types';

export interface FormApiField {
  id?: string | number;
  name: string;
  type: string | null;
  is_required?: boolean | null;
  display_order?: number | null;
  options?: string[] | null;
  sync_to_member?: boolean | null;
}

const OPTION_FIELD_TYPES = new Set<QuestionType>([
  QuestionType.DROP_DOWN,
  QuestionType.SINGLE_CHOICE,
  QuestionType.MULTIPLE_CHOICE,
  QuestionType.FILE_UPLOAD
]);

const mapFieldType = (type: string | null | undefined): QuestionType => {
  if (!type) {
    return QuestionType.TEXT;
  }
  const normalized = type.toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');
  if (normalized === 'dropdown') {
    return QuestionType.DROP_DOWN;
  }
  const match = Object.values(QuestionType).find(
    (value) => String(value).toLowerCase() === normalized
  );
  return match ?? QuestionType.TEXT;
};

const normalizeFieldName = (name: string) =>
  name.trim().toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');

export const isAlternateNumberField = (name: string): boolean => {
  const normalized = normalizeFieldName(name);
  return (
    normalized === 'alternate_number' ||
    normalized === 'alternate_mobile' ||
    normalized === 'alternate_phone' ||
    normalized === 'alternate_phone_number'
  );
};

export const isPrimaryMobileNumberField = (
  name: string,
  type: QuestionType
): boolean => {
  if (isAlternateNumberField(name)) {
    return false;
  }

  const normalized = normalizeFieldName(name);
  return (
    type === QuestionType.MOBILE_NUMBER ||
    normalized === 'mobile_number' ||
    normalized === 'primary_number'
  );
};

export const normalizeFormFieldLabel = (
  name: string,
  type: QuestionType
): string => {
  if (isAlternateNumberField(name)) {
    return 'Alternate number';
  }

  if (isPrimaryMobileNumberField(name, type)) {
    return 'Mobile number';
  }

  return name;
};

export const normalizeSubmissionFieldLabel = (
  fieldName: string,
  fieldType: string
): string => {
  if (isAlternateNumberField(fieldName)) {
    return 'Alternate number';
  }

  const normalizedName = normalizeFieldName(fieldName);
  const normalizedType = fieldType.toLowerCase().replace(/-/g, '_');
  if (
    (normalizedType === 'basic' || normalizedType === 'mobile_number') &&
    (normalizedName === 'mobile_number' || normalizedName === 'primary_number')
  ) {
    return 'Mobile number';
  }

  return fieldName;
};

export const inferFormFieldCategory = (
  name: string,
  type: QuestionType,
  syncToMember?: boolean | null
): FormFieldCategory => {
  const normalizedName = normalizeFieldName(name);
  if (
    normalizedName === 'registration_name' ||
    isPrimaryMobileNumberField(name, type)
  ) {
    return 'basic';
  }
  if (syncToMember) {
    return 'member';
  }
  return 'custom';
};

const inferFieldCategory = (
  field: FormApiField,
  fieldType: QuestionType
): FormFieldCategory =>
  inferFormFieldCategory(field.name, fieldType, field.sync_to_member);

const sortByDisplayOrder = (fields: FormApiField[]) =>
  fields
    .map((field, index) => ({ field, index }))
    .sort(
      (a, b) =>
        (a.field.display_order ?? a.index + 1) -
        (b.field.display_order ?? b.index + 1)
    );

export const normalizeFormFieldId = (
  id: string | number | undefined | null
): string => (id === undefined || id === null ? '' : String(id).trim());

const resolveFormFieldId = (
  field: FormApiField,
  idPrefix: string,
  sortedIndex: number,
  alwaysGenerateIds: boolean
) => {
  if (alwaysGenerateIds) {
    return `${idPrefix}-${sortedIndex + 1}`;
  }
  const apiId = normalizeFormFieldId(field.id);
  return apiId || `${idPrefix}-${sortedIndex + 1}`;
};

export interface MapFormApiFieldsOptions {
  alwaysGenerateIds?: boolean;
}

export const mapFormApiFields = (
  fields: FormApiField[],
  idPrefix = 'field',
  options?: MapFormApiFieldsOptions
): FormFieldData[] =>
  enrichMemberFormFields(
    sortByDisplayOrder(fields).map(({ field }, sortedIndex) => {
      const fieldType = mapFieldType(field.type);
      const fieldCategory = inferFieldCategory(field, fieldType);
      const apiOptions = Array.isArray(field.options) ? field.options : [];
      const order = field.display_order ?? sortedIndex + 1;
      const alwaysGenerateIds = options?.alwaysGenerateIds ?? false;

      return {
        id: resolveFormFieldId(field, idPrefix, sortedIndex, alwaysGenerateIds),
        label: normalizeFormFieldLabel(field.name, fieldType),
        type: fieldType,
        required: field.is_required ?? false,
        order,
        fieldCategory,
        ...(OPTION_FIELD_TYPES.has(fieldType) ? { options: apiOptions } : {}),
        syncToMember: field.sync_to_member ?? false,
        isLocked: fieldCategory === 'basic'
      };
    })
  );
